import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import useVisibilitySensor from '@rooks/use-visibility-sensor';
import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppDispatch } from '@/redux/hooks';
import { trackAdUnitClick, trackAdUnitVisible } from '@/redux/modules/analytics';
import { Ref } from '@/types/Ref';
import { addImageParams } from '@/utils/urls';

type Props = {
    destinationURL: string;
    featuredAuctioneerId: number;
    shouldUseHomePageRedesign?: boolean;
    houseId: number;
    houseName: string;
    imageURL: string;
};

const CONTROL_IMAGE_WIDTH = 184;
const CONTROL_IMAGE_HEIGHT = 104;
const CONTROL_CARD_PADDING = 5;
const REDESIGN_IMAGE_WIDTH = 246;
const REDESIGN_IMAGE_HEIGHT = 139;

export const AuctioneerCard = ({
    destinationURL,
    featuredAuctioneerId,
    shouldUseHomePageRedesign = false,
    houseId: sellerId,
    houseName,
    imageURL,
}: Props) => {
    const dispatch = useAppDispatch();
    const imageWidth = shouldUseHomePageRedesign ? REDESIGN_IMAGE_WIDTH : CONTROL_IMAGE_WIDTH;
    const imageHeight = shouldUseHomePageRedesign ? REDESIGN_IMAGE_HEIGHT : CONTROL_IMAGE_HEIGHT;
    const url = destinationURL.includes('liveauctioneers.com/auctioneer/')
        ? `/auctioneer${destinationURL.split('/auctioneer')[1]}`
        : destinationURL;
    const isInternalUrl = !url.includes('http');
    const safeUrl = imageURL.trim().replace(' ', '%20');
    const src = addImageParams({
        quality: 90,
        url: safeUrl,
        width: imageWidth,
    });
    const highDpiSrc = addImageParams({
        quality: 90,
        url: safeUrl,
        width: imageWidth * 2,
    });

    const targetRef: Ref = useRef<HTMLDivElement>(null);
    const { isVisible } = useVisibilitySensor(targetRef, {
        intervalCheck: false,
        resizeCheck: true,
        scrollCheck: true,
    });

    const [visibilityMessageSent, setVisibilityMessageSent] = useState(false);

    useEffect(() => {
        if (isVisible && !visibilityMessageSent && featuredAuctioneerId) {
            dispatch(
                trackAdUnitVisible({
                    adUnitId: featuredAuctioneerId,
                    page: 'home',
                })
            );
            setVisibilityMessageSent(true);
        }
    }, [dispatch, featuredAuctioneerId, isVisible, sellerId, visibilityMessageSent]);

    const trackClick = useCallback(() => {
        Boolean(featuredAuctioneerId) &&
            dispatch(
                trackAdUnitClick({
                    adUnitId: featuredAuctioneerId,
                    consignment: false,
                    page: 'home',
                })
            );
    }, [dispatch, featuredAuctioneerId]);

    const cardStyle: CSSProperties = shouldUseHomePageRedesign
        ? {
              aspectRatio: `${REDESIGN_IMAGE_WIDTH} / ${REDESIGN_IMAGE_HEIGHT}`,
              maxHeight: REDESIGN_IMAGE_HEIGHT,
              maxWidth: REDESIGN_IMAGE_WIDTH,
          }
        : {
              height: CONTROL_IMAGE_HEIGHT,
              padding: CONTROL_CARD_PADDING,
              width: CONTROL_IMAGE_WIDTH,
          };

    const cardContent = (
        <>
            <img
                alt={houseName}
                className={cn(shouldUseHomePageRedesign && 'size-full object-cover')}
                height={imageHeight}
                loading="lazy"
                src={src}
                srcSet={`${highDpiSrc} 2x`}
                style={
                    shouldUseHomePageRedesign
                        ? undefined
                        : { maxHeight: CONTROL_IMAGE_HEIGHT, maxWidth: CONTROL_IMAGE_WIDTH }
                }
                width={imageWidth}
            />
            <div
                className="pointer-events-none absolute inset-0 bg-transparent"
                ref={targetRef}
            />
        </>
    );

    return (
        <div
            className={cn(
                'relative flex cursor-pointer items-center justify-center',
                shouldUseHomePageRedesign && 'w-full overflow-hidden border border-border-secondary'
            )}
            data-testid="auctioneer-card"
            style={cardStyle}
        >
            {isInternalUrl ? (
                <RouteLink
                    className="flex h-full w-full items-center justify-center"
                    onClick={trackClick}
                    to={url}
                >
                    {cardContent}
                </RouteLink>
            ) : (
                <Link
                    className="flex h-full w-full items-center justify-center"
                    href={url}
                    onClick={trackClick}
                    target="_blank"
                >
                    {cardContent}
                </Link>
            )}
        </div>
    );
};
